import { Canvas } from '@react-three/fiber';
import { Suspense } from 'react';
import * as THREE from 'three';
import { WebGPURenderer } from 'three/webgpu';
import { RenderStats } from '@/dev/RenderStats';
import { Hero } from '@/hero/Hero';
import { StationAnchor } from '@/hero/StationAnchor';
import { useSelectionStore } from '@/state/selection';
import { stations, useTourStore } from '@/state/tour';
import { CameraRig } from './CameraRig';
import { Lights } from './lights';
import { Post } from './post';
import { Void } from './Void';

// ?webgl forces the WebGL 2 backend of the same renderer, the path browsers without WebGPU take.
async function makeRenderer({ canvas }: { canvas: unknown }) {
  const renderer = new WebGPURenderer({
    canvas: canvas as HTMLCanvasElement,
    antialias: false, // SMAA in the post stack instead
    powerPreference: 'high-performance',
    forceWebGL: new URLSearchParams(location.search).has('webgl'),
  });
  await renderer.init();
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.setClearColor('#07080a', 1);
  const backend = renderer.backend as { isWebGPUBackend?: boolean };
  useTourStore.getState().setBackend(backend.isWebGPUBackend ? 'WebGPU' : 'WebGL 2');
  return renderer;
}

export function Scene() {
  const setReady = useTourStore((s) => s.setReady);
  return (
    <div className="scene-layer" aria-hidden>
      <Canvas
        dpr={[1, 1.75]}
        gl={makeRenderer}
        camera={{ fov: 42, near: 0.1, far: 1200, position: [0, 6, 60] }}
        onPointerMissed={() => useSelectionStore.getState().select(null)}
        onCreated={() => setReady(true)}
      >
        <Suspense fallback={null}>
          <Void />
          <Lights />
          <Hero />
          {stations.map((s) => (
            <StationAnchor key={s.id} station={s} />
          ))}
          <CameraRig />
          <Post />
          <RenderStats />
        </Suspense>
      </Canvas>
    </div>
  );
}
